/**
 * Atlas PM · 项目详情（编辑）· 「自动创建任务」区块（P8.29：选模板 + 当场设周期）
 *
 * - GET /api/task-auto/projects/:projectId/tasks 展示已配置条目（模板名）+ 周期 + 最近一次执行
 * - [＋ 添加] → 小面板（内嵌 ProjectAutoTaskPicker）：模板 ▼ + 周期 ▼ + 周期日 ▼
 *   → POST /projects/:projectId/tasks，逐条回显：
 *     SUCCESS → 「已创建任务」 / SKIPPED → 后端 message（如「该周期已创建」）
 *     FAILED  → message 标红
 * - [停用] → 二次确认（「停用后不再自动创建新任务，已创建的任务保留」）→ PATCH { isEnabled:false }
 * - [启用] → PATCH { isEnabled:true }
 * - [改周期] → 小弹窗改周期类型 + 周期日（内联编辑）→ PATCH { cycleType, cycleDay }
 * - 无配置 → 「该项目未启用自动任务」+ [添加]
 *
 * ⚠️ 错误透传：保存 / 启停 / 改周期失败与逐条结果里的 message 均原样展示后端中文文案，
 *    不许改写成「操作失败」。
 */

import { useEffect, useState } from 'react';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Spinner from '../../components/Spinner';
import {
  AUTO_RUN_STATUS_LABEL,
  CYCLE_TYPE_LABEL,
  clampCycleDay,
  clampCycleMonth,
  cycleDayOptions,
  cycleMonthOptions,
  cyclePreviewText,
  type AutoCycleType,
  type AutoTaskInput,
  type ProjectAutoTaskItem,
  type SaveProjectAutoTaskResult,
} from './autoTaskApi';
import {
  useProjectAutoTasks,
  useSaveProjectTasks,
  useTaskTemplates,
  useUpdateProjectAutoTask,
} from './useAutoTask';
import ProjectAutoTaskPicker from './ProjectAutoTaskPicker';

export interface ProjectAutoTaskPanelProps {
  projectId: string;
}

/** 最近一次执行的展示文案（SUCCESS 绿 / SKIPPED 黄 / FAILED 红 + 后端原文） */
function lastRunText(item: ProjectAutoTaskItem): { text: string; tone: string } {
  const run = item.lastRun;
  if (!run) {
    return { text: item.isEnabled ? '未执行' : '已停用，未执行', tone: 'muted' };
  }
  const label = AUTO_RUN_STATUS_LABEL[run.status as keyof typeof AUTO_RUN_STATUS_LABEL] ?? run.status;
  let text = `上次 ${run.periodKey} · ${label}`;
  if (run.message) text += `（${run.message}）`;
  const tone =
    run.status === 'SUCCESS' ? 'is-ok' : run.status === 'FAILED' ? 'is-fail' : 'is-skip';
  return { text, tone };
}

/** POST /projects/:projectId/tasks 逐条结果文案 */
function resultText(r: SaveProjectAutoTaskResult): { text: string; tone: string } {
  if (r.status === 'SUCCESS') {
    const base = r.taskId ? `已创建任务（任务 #${r.taskId}）` : '已创建任务';
    return { text: r.message ? `${base}；${r.message}` : base, tone: 'is-ok' };
  }
  const reason = r.message ?? AUTO_RUN_STATUS_LABEL[r.status] ?? r.status;
  if (r.status === 'FAILED') return { text: `失败：${reason}`, tone: 'is-fail' };
  return { text: `已跳过：${reason}`, tone: 'is-skip' };
}

export default function ProjectAutoTaskPanel({ projectId }: ProjectAutoTaskPanelProps) {
  const tasksQ = useProjectAutoTasks(projectId);
  const templatesQ = useTaskTemplates();
  const saveMut = useSaveProjectTasks(projectId);
  const updateMut = useUpdateProjectAutoTask(projectId);

  // ── ＋ 添加（内嵌 Picker → POST） ──
  const [addOpen, setAddOpen] = useState(false);
  const [addItems, setAddItems] = useState<AutoTaskInput[]>([]);
  const [addResults, setAddResults] = useState<SaveProjectAutoTaskResult[] | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  // ── 停用 / 启用 ──
  const [toggleTarget, setToggleTarget] = useState<ProjectAutoTaskItem | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  // ── 改周期（内联编辑弹窗） ──
  const [cycleTarget, setCycleTarget] = useState<ProjectAutoTaskItem | null>(null);
  const [cycleType, setCycleType] = useState<AutoCycleType>('MONTHLY');
  const [cycleMonth, setCycleMonth] = useState<number | null>(null);
  const [cycleDay, setCycleDay] = useState(5);
  const [cycleError, setCycleError] = useState<string | null>(null);

  // 每次打开添加面板时清空上一次的选择 / 结果
  useEffect(() => {
    if (addOpen) {
      setAddItems([]);
      setAddResults(null);
      setAddError(null);
    }
    if (cycleTarget) {
      setCycleType(cycleTarget.cycleType);
      setCycleMonth(cycleTarget.cycleMonth ?? null);
      setCycleDay(cycleTarget.cycleDay);
      setCycleError(null);
    }
  }, [addOpen, cycleTarget]);

  const tasks = tasksQ.data ?? [];
  const templateNameOf = (templateId: string): string | null => {
    const t = (templatesQ.data ?? []).find((x) => String(x.id) === templateId);
    return t?.name ?? tasks.find((r) => String(r.templateId) === templateId)?.templateName ?? null;
  };

  const submitAdd = () => {
    if (addItems.length === 0) return;
    setAddError(null);
    saveMut.mutate(addItems, {
      onSuccess: (results) => setAddResults(results),
      onError: (err) =>
        setAddError(err instanceof Error ? err.message : '添加失败，请稍后重试'),
    });
  };

  const confirmToggle = () => {
    if (!toggleTarget) return;
    setToggleError(null);
    updateMut.mutate(
      { id: toggleTarget.id, body: { isEnabled: !toggleTarget.isEnabled } },
      {
        onSuccess: () => setToggleTarget(null),
        onError: (err) =>
          setToggleError(err instanceof Error ? err.message : '操作失败，请稍后重试'),
      },
    );
  };

  const openCycle = (item: ProjectAutoTaskItem) => {
    setCycleError(null);
    setCycleTarget(item);
  };

  const confirmCycle = () => {
    if (!cycleTarget) return;
    setCycleError(null);
    const body: {
      cycleType: AutoCycleType;
      cycleDay: number;
      cycleMonth?: number | null;
    } = {
      cycleType,
      cycleDay,
    };
    // ⚠️ PATCH 对 WEEKLY/MONTHLY 必须【省略】cycleMonth（传 null 也会被判定为
    //    「周/月周期不能设置周期月份」→ 400）；QUARTERLY/YEARLY 则必须带上。
    if (cycleType !== 'WEEKLY' && cycleType !== 'MONTHLY') {
      body.cycleMonth = clampCycleMonth(cycleType, cycleMonth) ?? 1;
    }
    updateMut.mutate(
      { id: cycleTarget.id, body },
      {
        onSuccess: () => setCycleTarget(null),
        onError: (err) =>
          setCycleError(err instanceof Error ? err.message : '保存失败，请稍后重试'),
      },
    );
  };

  return (
    <div className="pta-panel" data-testid="pta-panel">
      <div className="pta-head">
        <b>自动创建任务</b>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          data-testid="pta-add"
          onClick={() => setAddOpen(true)}
        >
          ＋ 添加
        </Button>
      </div>

      {tasksQ.isLoading ? (
        <Spinner label="加载自动任务配置…" />
      ) : tasks.length === 0 ? (
        <div className="pta-empty" data-testid="pta-empty">
          <span>该项目未启用自动任务</span>
          <Button
            type="button"
            variant="primary"
            size="sm"
            data-testid="pta-add-empty"
            onClick={() => setAddOpen(true)}
          >
            添加
          </Button>
        </div>
      ) : (
        <div className="pta-list" data-testid="pta-list">
          {tasks.map((r) => {
            const last = lastRunText(r);
            return (
              <div className="pta-row" key={r.id}>
                <div className="pta-row__main">
                  <b>{r.templateName ?? '未命名模板'}</b>
                  <span className="pta-cycle">{r.cycleText}</span>
                  {!r.isEnabled && <span className="pta-badge">已停用</span>}
                </div>
                <div className={`pta-row__last ${last.tone}`} data-testid="pta-last-run">
                  {last.text}
                </div>
                <div className="pta-row__actions">
                  <button
                    type="button"
                    className="pm-linkbtn"
                    data-testid="pta-edit-cycle"
                    onClick={() => openCycle(r)}
                  >
                    改周期
                  </button>
                  <button
                    type="button"
                    className={`pm-linkbtn ${r.isEnabled ? 'is-danger' : ''}`}
                    data-testid="pta-toggle"
                    onClick={() => {
                      setToggleError(null);
                      setToggleTarget(r);
                    }}
                  >
                    {r.isEnabled ? '停用' : '启用'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ⭐ ＋ 添加：内嵌 Picker + POST + 逐条结果 */}
      <Modal
        open={addOpen}
        title="添加自动创建任务"
        size="sm"
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={saveMut.isPending}>
              关闭
            </Button>
            <Button
              variant="primary"
              onClick={submitAdd}
              disabled={saveMut.isPending || addItems.length === 0}
              data-testid="pta-add-submit"
            >
              {saveMut.isPending ? '提交中…' : `确定添加（${addItems.length}）`}
            </Button>
          </>
        }
      >
        {addError && (
          <div className="pm-form__error" role="alert">
            ⚠ {addError}
          </div>
        )}

        <ProjectAutoTaskPicker value={addItems} onChange={setAddItems} />

        {addResults && (
          <div className="pta-results" data-testid="pta-results">
            {addResults.map((r) => {
              const t = resultText(r);
              return (
                <div className="pta-result" key={r.templateId || r.taskId}>
                  <b>{templateNameOf(r.templateId) ?? `模板 #${r.templateId}`}</b>
                  <span className={t.tone}>{t.text}</span>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* ⭐ 停用 / 启用 二次确认（仅停用展示确认文案） */}
      <Modal
        open={toggleTarget !== null}
        size="sm"
        danger={!toggleTarget?.isEnabled}
        title={`${toggleTarget?.isEnabled ? '停用' : '启用'}自动创建 · ${toggleTarget?.templateName ?? ''}`}
        onClose={() => setToggleTarget(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setToggleTarget(null)} disabled={updateMut.isPending}>
              取消
            </Button>
            <Button
              variant={toggleTarget?.isEnabled ? 'danger' : 'primary'}
              onClick={confirmToggle}
              disabled={updateMut.isPending}
              data-testid="pta-toggle-confirm"
            >
              {updateMut.isPending ? '提交中…' : toggleTarget?.isEnabled ? '确认停用' : '确认启用'}
            </Button>
          </>
        }
      >
        {toggleTarget?.isEnabled && (
          <div className="pm-delete-explain">
            停用后不再自动创建新任务，<b>已创建的任务保留</b>。
          </div>
        )}
        {toggleError && (
          <div className="pm-form__error" role="alert">
            ⚠ {toggleError}
          </div>
        )}
      </Modal>

      {/* ⭐ 改周期（内联编辑）= 周期类型 + 周期日 */}
      <Modal
        open={cycleTarget !== null}
        size="sm"
        title={`改周期 · ${cycleTarget?.templateName ?? ''}`}
        onClose={() => setCycleTarget(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCycleTarget(null)} disabled={updateMut.isPending}>
              取消
            </Button>
            <Button
              variant="primary"
              onClick={confirmCycle}
              disabled={updateMut.isPending}
              data-testid="pta-cycle-confirm"
            >
              {updateMut.isPending ? '保存中…' : '保存'}
            </Button>
          </>
        }
      >
        <div className="pta-cycle-edit">
          <div className="pta-pick__field">
            <span className="pta-pick__label">周期</span>
            <select
              className="pm-select"
              data-testid="pta-cycle-type"
              value={cycleType}
              onChange={(e) => {
                const ct = e.target.value as AutoCycleType;
                setCycleType(ct);
                setCycleDay(clampCycleDay(ct, cycleDay));
                setCycleMonth(
                  ct === 'WEEKLY' || ct === 'MONTHLY'
                    ? null
                    : clampCycleMonth(ct, cycleMonth),
                );
              }}
            >
              {(Object.keys(CYCLE_TYPE_LABEL) as AutoCycleType[]).map((ct) => (
                <option key={ct} value={ct}>
                  {CYCLE_TYPE_LABEL[ct]}
                </option>
              ))}
            </select>
          </div>
          {cycleMonthOptions(cycleType).length > 0 && (
            <div className="pta-pick__field" data-testid="pta-cycle-month-field">
              <span className="pta-pick__label">月份</span>
              <select
                className="pm-select"
                data-testid="pta-cycle-month"
                value={clampCycleMonth(cycleType, cycleMonth) ?? 1}
                onChange={(e) => setCycleMonth(Number(e.target.value))}
              >
                {cycleMonthOptions(cycleType).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="pta-pick__field">
            <span className="pta-pick__label">周期日</span>
            <select
              className="pm-select"
              data-testid="pta-cycle-day"
              value={cycleDay}
              onChange={(e) => setCycleDay(Number(e.target.value))}
            >
              {cycleDayOptions(cycleType).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="pm-field__hint" data-testid="pta-cycle-preview">
          {cyclePreviewText(cycleType, cycleMonthOptions(cycleType).length > 0 ? cycleMonth : null, cycleDay)}
        </div>
        <div className="pm-field__hint">
          只改周期配置，不会重建历史任务，也不会变更已创建的任务。
        </div>
        {cycleError && (
          <div className="pm-form__error" role="alert">
            ⚠ {cycleError}
          </div>
        )}
      </Modal>
    </div>
  );
}